<template>
  <BField
    class="d-searchInput"
    :class="initialStyle"
  >
    <div
      class="control"
      v-if="dropdown"
    >
      <BDropdown
        :mobile-modal="false"
        v-model="attributes.searchType"
      >
        <button
          class="button d-searchInput_dropdownButton"
          slot="trigger"
        >
          <span>{{ currentSearchType.text }}</span>
          <CoconalaIcon
            class="d-searchInput_dropdownIcon"
            name="chevron-down"
          />
        </button>
        <BDropdownItem
          v-for="type in SEARCH_TYPES"
          :value="type.value"
          :key="type.value"
          class="d-searchInput_dropdownItem"
        >{{ type.text }}{{ type.suffix }}</BDropdownItem>
      </BDropdown>
    </div>
    <form
      action="#"
      class="control d-searchInput_input"
      @submit.prevent
    >
      <BInput
        type="search"
        autocomplete="off"
        v-model="attributes.searchAttributes.searchText"
        :expanded="expanded"
        :placeholder="initialPlaceholder"
        ref="searchInput"
        @focus="focusSearchInput"
        @keydown.native="onKeyDown"
        @keyup.native="onKeyUp"
        @compositionstart.native="composing=true"
        @compositionend.native="composing=false"
      />
      <CoconalaIcon
        v-if="isShowClearButton"
        @click.native="onClearInput"
        class="d-searchInput_clearButton"
        :name="isSP ? 'times-circle' : 'cross'"
      />
      <div
        class="d-searchInput_suggest"
        ref="suggest"
        v-if="suggestResult.length > 0 && attributes.searchAttributes.searchText"
      >
        <SuggestList
          :search-text="attributes.searchAttributes.searchText"
          :focused-index="focusedIndex"
          :suggests="suggestResult"
          @select="selectSuggest"
          @focus="setFocusedIndex"
          @focusOut="clearFocusedIndex"
        />
      </div>
    </form>
    <div class="control">
      <button
        class="button d-searchInput_searchButton"
        @click="doSearch"
      >
        <CoconalaIcon
          name="search"
          color="ds2Primary"
          size="17px"
        />
      </button>
    </div>
  </BField>
</template>
<script>
import SuggestList from '~/components/molecules/SuggestList'
import { debounce } from 'lodash-es'
// vuex
import { mapGetters, mapMutations, mapState } from 'vuex'

export const SEARCH_TYPES = Object.freeze([
  { value: 'service', text: 'サービス', suffix: 'を探す' },
  { value: 'user', text: '出品者', suffix: 'を探す' },
  { value: 'request', text: '仕事・相談', suffix: 'を探す' },
  { value: 'blog', text: 'ブログ', suffix: 'を探す' }
])

export default {
  name: 'DSearchInput',
  components: {
    SuggestList
  },
  props: {
    initialSearchType: {
      type: String,
      default: SEARCH_TYPES[0].value
    },
    initialSearchText: {
      type: String
    },
    initialPlaceholder: {
      type: String,
      default: null
    },
    initialStyle: {
      type: String,
      default: null
    },
    expanded: {
      type: Boolean,
      default: false
    },
    dropdown: {
      type: Boolean,
      default: true
    },
    suggestResult: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      SEARCH_TYPES,
      attributes: {
        searchType: this.initialSearchType,
        searchAttributes: {
          searchText: this.$route.query.keyword,
          searchCategoryId: this.$route.query.category_id
        }
      },
      suggest: {
        baseQuery: ''
      },
      composing: false,
      focusedIndex: null
    }
  },
  watch: {
    '$route.query.keyword'(to) {
      this.attributes.searchAttributes.searchText = to
    },
    '$route.query.category_id'(to) {
      this.attributes.searchAttributes.searchCategoryId = to
    },
    '$route.path'() {
      // createdだけではNuxtLinkでページ遷移した時に対応できないため
      this.setSearchType()
    },
    suggestResult(result) {
      if (this.isSP) {
        if (result.length !== 0) {
          this.$util.bodyScrollPrevent(true)
        } else {
          this.$util.bodyScrollPrevent(false)
        }
      }
      this.focusedIndex = null
    }
  },
  created() {
    this.setSearchType()
    this.setSearchText()
  },
  methods: {
    ...mapMutations('my/header', ['SAVE_USER_INPUT_SERCH_TEXT']),
    setSearchType() {
      const pagePath = this.$route.path
      if (pagePath.indexOf('/users') === 0) {
        this.attributes.searchType = this.SEARCH_TYPES[1].value
      }
      if (pagePath.indexOf('/requests') === 0) {
        this.attributes.searchType = this.SEARCH_TYPES[2].value
      }
      if (pagePath.indexOf('/blogs') === 0) {
        this.attributes.searchType = this.SEARCH_TYPES[3].value
      }
    },
    setSearchText() {
      if (!this.userInputSearchText) {
        return
      }
      this.attributes.searchAttributes.searchText = this.userInputSearchText
    },
    doSearch() {
      if (this.composing) return // IME確定時のenterでは検索しない
      if (this.focusedIndex !== null) {
        // サジェストの内容で検索された時はログを送信する
        const suggestLogParam = {
          baseQuery: this.suggest.baseQuery,
          suggestion: JSON.stringify(this.suggestResult),
          selected: JSON.stringify(this.suggestResult[this.focusedIndex])
        }
        this.$emit('selectSuggest', suggestLogParam)
      } else {
        // サジェストの内容以外で検索を実施するときはカテゴリIDを空にする
        this.attributes.searchAttributes.searchCategoryId = ''
      }

      if (this.attributes.searchAttributes.searchText) {
        this.attributes.searchAttributes.searchText = this.attributes.searchAttributes.searchText.trim()
      }
      this.$emit('change', this.attributes)
    },
    doSuggest(keyword) {
      const INTERVAL_TIME = 200
      debounce(() => {
        this.suggest.baseQuery = keyword
        this.$emit('suggest', keyword)
      }, INTERVAL_TIME)()
    },
    clearSuggest() {
      this.$emit('clearSuggest')
      this.focusedIndex = null
    },
    selectSuggest(suggest) {
      this.attributes.searchAttributes.searchText = suggest.title
      this.attributes.searchAttributes.searchCategoryId = suggest.masterCategoryId
      this.composing = false
      this.doSearch()
    },
    setFocusedIndex(index) {
      this.focusedIndex = index
    },
    clearFocusedIndex() {
      this.focusedIndex = null
    },
    moveCaretEnd() {
      this.$refs.searchInput.focus()
      const inputForm = this.$refs.searchInput.$el.getElementsByTagName('input')[0]
      if (inputForm.selectionStart) {
        inputForm.selectionStart = inputForm.selectionEnd = inputForm.value.length
      }
    },
    suggestUp() {
      if (!this.suggestResult.length) {
        return
      }
      if (this.focusedIndex === null) {
        this.focusedIndex = this.suggestResult.length - 1
      } else {
        if (this.focusedIndex === 0) {
          return
        }
        this.focusedIndex--
      }
      this.attributes.searchAttributes.searchText =
        this.suggestResult[this.focusedIndex].title + ' '
      this.attributes.searchAttributes.searchCategoryId = this.suggestResult[
        this.focusedIndex
      ].masterCategoryId
    },
    suggestDown() {
      if (!this.suggestResult.length) {
        return
      }
      if (this.focusedIndex === null) {
        this.focusedIndex = 0
      } else {
        if (this.focusedIndex === this.suggestResult.length - 1) {
          return
        }
        this.focusedIndex++
      }
      this.attributes.searchAttributes.searchText =
        this.suggestResult[this.focusedIndex].title + ' '
      this.attributes.searchAttributes.searchCategoryId = this.suggestResult[
        this.focusedIndex
      ].masterCategoryId
    },
    onClearInput() {
      this.attributes.searchAttributes.searchText = ''
      this.attributes.searchAttributes.searchCategoryId = ''
      this.clearSuggest()
    },
    onKeyUp(e) {
      const keyCode = e.keyCode
      switch (keyCode) {
        case 13: // Enter
          break
        case 27: // Escape
          this.clearSuggest()
          break
        case 37: // 左
          break
        case 38: // 上
          e.preventDefault()
          this.suggestUp()
          this.moveCaretEnd()
          break
        case 39: // 右
          break
        case 40: // 下
          e.preventDefault()
          this.suggestDown()
          this.moveCaretEnd()
          break
        default:
          if (this.attributes.searchType === SEARCH_TYPES[0].value) {
            const searchText = this.attributes.searchAttributes.searchText
            if (searchText) {
              this.doSuggest(searchText)
            } else {
              this.clearSuggest()
            }
          }
          break
      }
    },
    onKeyDown(e) {
      const keyCode = e.keyCode
      switch (keyCode) {
        case 13: // Enter
          this.doSearch()
          break
        case 38: // 上
          e.preventDefault()
          break
      }
    },
    focusSearchInput() {
      if (
        !this.attributes.searchAttributes.searchText ||
        this.attributes.searchType !== SEARCH_TYPES[0].value
      ) {
        return
      }
      // サービス検索で検索窓に入力がある場合サジェストを表示する
      this.doSuggest(this.attributes.searchAttributes.searchText)
    }
  },
  computed: {
    ...mapGetters('ui', ['isSP']),
    ...mapState('my/header', ['userInputSearchText']),
    currentSearchType() {
      return SEARCH_TYPES.find(t => t.value === this.attributes.searchType)
    },
    isShowClearButton() {
      return this.attributes.searchAttributes.searchText
    }
  },
  mounted() {
    window.addEventListener(
      'click',
      (this._onBlurHandler = event => {
        if (!this.$refs.suggest) {
          return
        }
        if (this.$refs.suggest.contains(event.target)) {
          return
        }
        this.clearSuggest()
      })
    )
  },
  beforeDestroy() {
    window.removeEventListener('click', this._onBlurHandler)
    this.SAVE_USER_INPUT_SERCH_TEXT(this.attributes.searchAttributes.searchText)
    // NOTE: レアケースではあるが、nuxt-link遷移発生時にページ固定が解除されない可能性があるので念のため固定解除する
    this.$util.bodyScrollPrevent(false)
  }
}
</script>
<style lang="scss" scoped>
$searchClearBtnSize: 10px;

/deep/ .button,
/deep/ .input {
  height: 34px;
  border-color: $ds2-color-gray-200;
  font-size: 13px;
}

/deep/ .input {
  border-right: 0;
  font-size: 14px;

  &::placeholder {
    color: $ds2-color-gray-400;
  }
}

.d-searchInput {
  &_dropdownButton {
    padding: 0 10px 0 14px;
    border-radius: 6px;
    color: $ds2-color-gray-600;
    font-weight: bold;

    > span {
      margin-top: 1px;
    }
  }

  &_dropdownIcon {
    display: inline-block;
    font-size: 16px;
    transform: scale(0.5);

    &::before {
      vertical-align: -2px;
    }
  }

  &_dropdownItem {
    color: $ds2-color-gray-900;
    font-size: 13px;

    &.is-active {
      background-color: $ds2-color-brand-primary;
    }
  }

  &_input {
    position: relative;

    flex-grow: 1;

    /deep/ input[type='search'] {
      padding-right: 24px;
      color: $ds2-color-gray-900;

      &::-webkit-search-cancel-button,
      &::-webkit-search-decoration {
        display: none;
      }

      &::-ms-clear {
        visibility: hidden;
      }
    }
  }

  &_clearButton {
    position: absolute;
    top: calc(50% - #{$searchClearBtnSize / 2});
    right: 4px;
    z-index: 5;
    color: $ds2-color-gray-600;
    font-size: $searchClearBtnSize;
    cursor: pointer;
  }

  &_suggest {
    position: absolute;
    z-index: z(header, suggest);
    box-sizing: border-box;
    width: calc(100% + 46px);
    border-radius: 4px;
    background: $ds2-color-white;
    box-shadow: -1px 1px 5px rgba(0, 0, 0, 0.25);
    font-size: 13px;
  }

  &_searchButton {
    @include link-effect;

    width: 48px;
    border-left: 0;
    border-radius: 6px;

    .coconala-icon {
      &::before {
        vertical-align: -2px;
      }
    }
  }
}

@media (max-width: breakpoint(Header, S)) {
  /deep/ .button {
    font-size: 11px;
  }

  /deep/ .input {
    border-right: 1px solid $ds2-color-gray-200;
    border-top-right-radius: 6px !important;
    border-bottom-right-radius: 6px !important;
    font-size: 12px;
  }

  .d-searchInput {
    &_input {
      /deep/ input[type='search'] {
        padding-right: 22px;
      }
    }

    &_suggest {
      position: fixed;
      bottom: 0;
      left: 0;
      z-index: z(header, suggest);
      box-sizing: border-box;
      width: 100vw;
      height: calc(100% - 59px); //headerの高さを引く
      border: none;
      background: $color-white;
      box-shadow: none;
      font-size: 13px;
    }

    &_dropdownIcon {
      &::before {
        vertical-align: -3px;
      }
    }

    &_dropdownItem {
      line-height: 28px;
    }

    &_clearButton {
      right: 8px;
      color: $ds2-color-silver;
    }

    &_searchButton {
      display: none;
    }
  }
}
</style>
